import { Children, CSSProperties, isValidElement, ReactNode } from 'react'
import ReactSelect, { SingleValue } from 'react-select'

import Label from 'common/components/label'

interface OptionProps {
  value: string | number
  children?: ReactNode
  label?: string | number
  disabled?: boolean
  [key: string]: unknown
}

// Stub — rendered by Select parent which reads props directly
export const Option = (_props: OptionProps): null => null

type OptionType = { value: string; label: string; isDisabled?: boolean }

const selectStyles: any = {
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? '#15020c' : '#e0c797',
    borderRadius: '2px',
    backgroundColor: state.isDisabled ? '#f5f5f5' : '#fffdf9',
    boxShadow: 'none',
    '&:hover': { borderColor: '#15020c' },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? '#901356'
      : state.isFocused
        ? '#f5f5f5'
        : undefined,
    color: state.isSelected ? '#fff' : '#1f2937',
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
}

interface Props {
  id?: string
  label?: string
  value?: string | number
  defaultValue?: string | number
  placeholder?: string | ReactNode
  className?: string

  onChange: (value: any) => void
  onFocus?: () => void
  allowClear?: boolean
  showSearch?: boolean
  loading?: boolean
  disabled?: boolean
  style?: CSSProperties
  dataTestId?: string
  children?: ReactNode
  // absorbed antd-specific props
  mode?: string
  size?: string
  filterOption?: unknown
  optionFilterProp?: string
  showArrow?: boolean
  open?: boolean
  dropdownClassName?: string
  dropdownRender?: unknown
  menuItemSelectedIcon?: ReactNode
  maxTagCount?: number
  maxTagPlaceholder?: string
  'data-cy'?: string
  'data-testid'?: string
  [key: string]: unknown
}

const SelectComponent = ({
  id,
  label,
  children,
  style,
  dataTestId,
  'data-testid': dataTestIdAttr,
  'data-cy': _dataCy,
  value,
  defaultValue,
  placeholder,
  className,
  onChange,
  onFocus,
  allowClear = false,
  showSearch = false,
  loading = false,
  disabled = false,
  mode: _mode,
  size: _size,
  filterOption: _filterOption,
  optionFilterProp: _optionFilterProp,
  showArrow: _showArrow,
  open: _open,
  dropdownClassName: _dropdownClassName,
  dropdownRender: _dropdownRender,
  menuItemSelectedIcon: _menuItemSelectedIcon,
  maxTagCount: _maxTagCount,
  maxTagPlaceholder: _maxTagPlaceholder,
}: Props) => {
  const options: OptionType[] = Children.toArray(children)
    .filter(isValidElement)
    .map((child) => {
      const p = child.props as OptionProps
      const optLabel =
        typeof p.label === 'string' || typeof p.label === 'number'
          ? String(p.label)
          : typeof p.children === 'string' || typeof p.children === 'number'
            ? String(p.children)
            : String(p.value)
      return {
        value: String(p.value),
        label: optLabel,
        isDisabled: !!p.disabled,
      }
    })

  const selectedOption =
    value != null
      ? (options.find((o) => o.value === String(value)) ?? null)
      : null
  const defaultOption =
    defaultValue != null
      ? (options.find((o) => o.value === String(defaultValue)) ?? undefined)
      : undefined

  return (
    <>
      {label && <Label>{label}</Label>}
      <div
        className="updated-component"
        style={style}
        data-testid={dataTestId ?? dataTestIdAttr}
        aria-disabled={disabled ? 'true' : undefined}
      >
        <ReactSelect<OptionType>
          inputId={id}
          options={options}
          value={selectedOption}
          defaultValue={defaultOption}
          placeholder={placeholder}
          onChange={(option: SingleValue<OptionType>) =>
            onChange(option?.value)
          }
          onFocus={onFocus}
          isClearable={allowClear}
          isSearchable={showSearch}
          isLoading={loading}
          isDisabled={disabled}
          className={className}
          styles={selectStyles}
          menuPortalTarget={document.body}
        />
      </div>
    </>
  )
}

export default SelectComponent
